<template>
  <div class="post-search-page d-flex flex-column h-100">
    <div class="d-flex justify-content-between align-items-center mb-0 p-2">
      <h1 id="tour-contacts-title" class="mb-0">{{ $t('contacts.title') }}</h1>
      <div class="d-flex justify-content-end">
        <feather-icon
          v-b-tooltip="{ title: $t('common.refresh'), placement: 'bottom', trigger: 'hover' }"
          icon="RefreshCwIcon"
          size="22"
          class="cursor-pointer text-body"
          :class="{ 'fa-spin': refreshing }"
          @click="refreshing= true; getContacts()"
        />
      </div>
    </div>

    <section class="flex-grow-1 d-flex flex-column" style="min-height: 0;">
      <div v-if="isFetching" class="flex-grow-1 d-flex justify-content-center align-items-center">
        <b-spinner variant="primary" />
      </div>

      <div v-if="!isFetching && contacts && contacts.length > 0" class="d-flex flex-column w-100 flex-grow-1 overflow-auto">
        <div
          v-for="(contact, index) in contacts.slice((currentPage - 1) * limit, currentPage * limit)"
          :id="'tour-contact-' + (contact.is_blocked ? 'blocked' : 'normal')"
          :key="index"
          class="contact-list-item d-flex align-items-center justify-content-between"
        >
          <div class="d-flex align-items-center overflow-hidden flex-grow-1 mr-2">

            <img
              v-if="contact.is_organisation"
              :src="contact.profile_picture_url"
              :class="['avatar-circle', 'flex-shrink-0', { 'blocked-avatar': contact.is_blocked }]"
              style="object-fit: cover;"
              alt="Profile Picture"
            >
            <div
              v-else
              :class="['avatar-circle', 'flex-shrink-0', { 'blocked-avatar': contact.is_blocked }]"
            >
              {{ contact.firstname.charAt(0).toUpperCase() }}
            </div>
            <h5 class="mb-0 font-weight-bold text-body text-truncate" style="margin-left: 8px;">
              {{ contact.firstname }} {{ contact.lastname }}
            </h5>
          </div>

          <div class="flex-shrink-0">
            <b-btn
              v-if="!contact.is_blocked"
              size="sm"
              class="px-2 btn-purple"
              pill
              @click="showBlockModal(contact, 'blockUser')"
            >
              {{ $t('contacts.blockBtn') }}
            </b-btn>
            <b-btn
              v-else
              size="sm"
              variant="danger"
              pill
              class="px-2"
              @click="showBlockModal(contact, 'unBlockUser')"
            >
              {{ $t('contacts.unblockBtn') }}
            </b-btn>
          </div>
        </div>
      </div>

      <div v-if="!isFetching && (contacts.length > limit || contacts.length === 0)" class="border-top flex-shrink-0">
        <b-row v-if="contacts.length > limit" class="p-1">
          <b-col md="6" class="m-1">
            <b-pagination v-model="currentPage" :total-rows="contacts.length" :per-page="limit" class="my-0" />
          </b-col>
        </b-row>

        <div v-if="contacts.length === 0" class="d-flex flex-column justify-content-center align-items-center text-center p-3">
          <h5 class="mb-2">{{ $t('contacts.noContactsTitle') }}</h5>
          <p class="text-muted px-4">{{ $t('contacts.noContactsDescription') }}</p>
        </div>
      </div>
    </section>

    <b-modal id="blockUser" title="" hide-footer>
      <block-contact
        v-if="activeContact"
        :contact="activeContact"
        @close="closeModals()"
        @ok="closeAndRefresh()"
      />
    </b-modal>

    <b-modal id="unBlockUser" title="" hide-footer>
      <unblock-contact
        v-if="activeContact"
        :contact="activeContact"
        @close="closeModals"
        @ok="closeAndRefresh()"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ContactService from '@/services/ContactService'
import HelperService from '@/services/HelperService'
import BlockContact from '@/views/contacts/BlockContact.vue'
import UnblockContact from '@/views/contacts/UnblockContact.vue'
import { BSpinner } from 'bootstrap-vue'
import { TOUR_TARGETS } from '@/services/OnboardingService';

export default {
  name: 'Contacts',
  components: {
    BlockContact,
    UnblockContact,
    BSpinner,
  },
  data() {
    return {
      helperService: new HelperService(),
      isFetching: true,
      refreshing: false,
      contacts: [],
      currentPage: 1,
      limit: 10,
      activeContact: null,
      // Mock Contacts for onboarding
      dummyContacts: [
        { firstname: 'Joe', lastname: 'Bloggs', is_blocked: false },
        { firstname: 'Mary', lastname: 'Bloggs', is_blocked: true },
      ],
    }
  },
  computed: {
    ...mapState({}),
    isOnboarding() {
      return this.$store.state.app.isOnboarding;
    },
  },
  watch: {
    currentPage() {
      this.getContacts()
    },
    isOnboarding(val) {
      if (val) this.contacts = this.dummyContacts;
      else this.getContacts();
    },
  },
  mounted() {
    this.getContacts();

    if (this.isOnboarding) {
      // Wait for the tab animation and dummy data to load
      if (this.isOnboarding) {
        this.$root.$emit('tour-resume', TOUR_TARGETS.CONTACTS_TITLE);
      }
    }
  },
  methods: {
    getContacts() {
      if (this.isOnboarding) {
        this.contacts = this.dummyContacts;
        this.isFetching = false;
        return;
      }
      this.isFetching = true;
      ContactService.getAll()
        .then(res => {
          this.contacts = res.data;
        })
        .catch(err => {
          console.error('Error getting contacts ', err)
          this.$toast.error(this.$t('contacts.error.fetch'), {
            toastClassName: ['toast-std', 'warning-toast'],
          })
        })
        .finally(() => {
          this.isFetching = false;
          this.refreshing = false;
        })
    },
    showBlockModal(contact, modalName) {
      this.activeContact = contact
      this.$bvModal.show(modalName)
    },
    closeModals() {
      this.$bvModal.hide('blockUser')
      this.$bvModal.hide('unBlockUser')
    },
    closeAndRefresh() {
      this.closeModals();
      this.getContacts();
    },
  },
}
</script>

<style lang="scss">
.contact-list-item {
  padding: 1rem 1.5rem;
  background-color: transparent;
  border-bottom: 1px solid rgba(128, 128, 128, 0.2);

  &:last-child {
    border-bottom: none;
  }
}
.avatar-circle.blocked-avatar {
  background-color: #ea5455 !important;
  color: #ffffff !important;
}

.btn-purple {
  background-color: #a72bbc !important;
  border-color: #9c27b0 !important;
  color: #ffffff !important;

  &:hover, &:focus, &:active {
    background-color: #8a23b6 !important;
    border-color: #7b1fa2 !important;
    color: #ffffff !important;
    box-shadow: none !important;
  }
}
</style>
